
import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import Board from './Board';
import TreatyRoom from './TreatyRoom';
import NegotiatorGame from './NegotiatorGame';
import { VERSION, BUILD_DATE } from './version.js';

const SERVER_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';

const socket = io(SERVER_URL, {
  withCredentials: true
});

function App() {
  // Allow configuration of initial game state via env var
  // VITE_INITIAL_GAME can be 'menu', 'y', 'negotiator', etc. Defaults to 'landing'.
  const initialGameState = import.meta.env.VITE_INITIAL_GAME || 'menu';
  const [gameState, setGameState] = useState(initialGameState); // landing, menu, waiting, playing, y, negotiator, etc.
  const [gameId, setGameId] = useState('');
  const [gameType, setGameType] = useState('y'); // Track the current game type
  const [inputGameId, setInputGameId] = useState('');
  const [playerColor, setPlayerColor] = useState(null);
  const [board, setBoard] = useState({});
  const [turn, setTurn] = useState('white');
  const [statusMessage, setStatusMessage] = useState('');
  const [user, setUser] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  const [players, setPlayers] = useState([]);
  const [boardSize, setBoardSize] = useState(10);
  const [isCreating, setIsCreating] = useState(false);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [timeUsed, setTimeUsed] = useState({ white: 0, black: 0 });
  const [currentTurnTime, setCurrentTurnTime] = useState(0);
  const [holdState, setHoldState] = useState(null); // { initiator: 'white'|'black', active: boolean }
  const [resumeRequestedBy, setResumeRequestedBy] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(null); // null = live, number = index in moveHistory
  const [handicap, setHandicap] = useState(0);
  const [isPersistent, setIsPersistent] = useState(false);
  const [ongoingGames, setOngoingGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGameType, setSelectedGameType] = useState('y');
  const [isJoining, setIsJoining] = useState(false);
  const [waitingGames, setWaitingGames] = useState([]);
  const [versionInfo, setVersionInfo] = useState(null);
  const [isHandicapPhase, setIsHandicapPhase] = useState(false);
  const [isLocal, setIsLocal] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInitialMoveBanner, setShowInitialMoveBanner] = useState(false);
  const [winningPath, setWinningPath] = useState(null);
  const [treatyRoomOpen, setTreatyRoomOpen] = useState(false);
  const [negotiationPartner, setNegotiationPartner] = useState(null);
  // NEGOTIATOR game settings
  const [selectedMap, setSelectedMap] = useState('neo-singapore');
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [turnLength, setTurnLength] = useState('30min');
  const [negotiatorMap, setNegotiatorMap] = useState(null);
  const [urlGameId, setUrlGameId] = useState(null); // Store game ID from URL for post-login joining
  const turnStartTimeRef = useRef(null);

  // Helper to copy join URL
  const handleCopyUrl = () => {
    const joinUrl = window.location.origin + '/' + gameId;
    navigator.clipboard.writeText(joinUrl).then(() => {
      setCopySuccess('Copied!');
      setTimeout(() => setCopySuccess(''), 1500);
    }, () => {
      setCopySuccess('Failed to copy');
      setTimeout(() => setCopySuccess(''), 1500);
    });
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      socket.emit('visibilityChange', { visible: document.visibilityState === 'visible' });

      if (document.visibilityState === 'hidden') {
        if (gameState === 'playing' && (!holdState || !holdState.active)) {
             requestHold();
        }
      } else if (document.visibilityState === 'visible') {
        // If I come back and it's my turn, auto-request resume
        // OR if there is a pending resume request from the opponent
        if (gameState === 'playing' && holdState && holdState.active) {
            if (turn === playerColor) {
                requestResume();
            } else if (resumeRequestedBy && resumeRequestedBy !== playerColor) {
                requestResume();
            }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [gameState, holdState, turn, playerColor, resumeRequestedBy]);

  useEffect(() => {
    // If hold is active, timer should stop if it's the held player's turn
    // Or if the game is fully paused (after opponent finished turn)
    const isPaused = holdState && holdState.active && (turn === holdState.initiator || turn !== holdState.initiator); 
    // Actually, if hold is active:
    // 1. Initiator's turn: Paused immediately.
    // 2. Opponent's turn: Not paused until they move.
    
    const isTimerRunning = gameState === 'playing' && (isLocal || turn === playerColor) && 
        (!holdState || !holdState.active || (holdState.active && holdState.initiator !== playerColor));

    if (isTimerRunning) {
        turnStartTimeRef.current = Date.now();
    } else {
        turnStartTimeRef.current = null;
    }
  }, [gameState, turn, playerColor, holdState]);

  useEffect(() => {
    let timerInterval;
    const isFirstMove = Object.keys(board).length === 0;
    const isFirstRealMove = moveHistory.every(m => m.isHandicap);

    // Timer logic: run timer when it's your turn, game is playing, and not in handicap phase
    // Stop timer only when hold is active AND it's not your turn
    const shouldRunTimer = gameState === 'playing' && 
        turn === (isLocal ? turn : playerColor) && 
        !isHandicapPhase &&
        (!holdState || !holdState.active || (holdState.active && turn === playerColor));

    if (shouldRunTimer) {
        // Don't start timer if it's the very first move of a standard game (White's turn, empty board)
        // If handicap moves exist (moveHistory.length > 0), we DO want the timer to run.
        if (turn === 'white' && isFirstRealMove && moveHistory.length === 0) {
            setCurrentTurnTime(0);
            setShowInitialMoveBanner(true);
            return;
        }
    } else {
        // If paused, stop updating
        setShowInitialMoveBanner(false);
    }
    return () => clearInterval(timerInterval);
  }, [turn, gameState, board, holdState, isHandicapPhase, moveHistory, isLocal]);

  useEffect(() => {
    socket.on('connect', () => {
      console.log("Socket connected");
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log("Socket disconnected");
      setIsConnected(false);
    });

    // Check for logged in user and handle URL parameters
    fetch(`${SERVER_URL}/api/user`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setUser(data);
        
        // Check if there's a join parameter in the URL
        const urlParams = new URLSearchParams(window.location.search);
        const joinGameId = urlParams.get('join');
        
        if (joinGameId) {
          // Auto-join the game
          console.log('Auto-joining game from URL:', joinGameId);
          setGameState('menu'); // Set state first so page isn't blank
          console.log('🎯 Set gameState to menu, waiting for socket...');
          // Wait a moment for socket to be ready then join
          setTimeout(() => {
            console.log('🚀 Emitting joinGame event for:', joinGameId);
            socket.emit('joinGame', joinGameId);
          }, 500);
          // Clear the URL parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        } else if (data && data.id) {
          // User is authenticated, go to lobby
          setGameState('menu');
        } else {
          // User not authenticated, stay on landing
          setGameState('landing');
        }
      })
      .catch(err => {
        console.error("Failed to fetch user", err);
        // Even on error, check for URL join
        const urlParams = new URLSearchParams(window.location.search);
        const joinGameId = urlParams.get('join');
        
        if (joinGameId) {
          console.log('Auto-joining game from URL (after error):', joinGameId);
          setGameState('menu');
          setTimeout(() => {
            socket.emit('joinGame', joinGameId);
          }, 500);
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setGameState('landing');
        }
      });

    socket.on('gameCreated', ({ gameId, color, boardSize, gameType }) => {
      console.log("Game created event received:", gameId, color, boardSize, gameType);
      setGameId(gameId);
      setPlayerColor(color);
      setBoardSize(boardSize);
      setGameType(gameType || 'y');
      setGameState('waiting');
      setStatusMessage(`Game created! ID: ${gameId}. Waiting for ${gameType === 'negotiator' ? 'players' : 'opponent'}...`);
      
      // For NEGOTIATOR games, initialize players array with the creator
      if (gameType === 'negotiator') {
        console.log('Setting players for NEGOTIATOR game, socket.id:', socket.id);
        const playersList = [{
          id: socket.id,
          name: `Guest-${socket.id.substring(0, 4)}`,
          color: color
        }];
        console.log('Created players list:', playersList);
        setPlayers(playersList);
      }
    });

    socket.on('gameJoined', ({ gameId, color, boardSize, gameType }) => {
      console.log('🎮 Game joined event received:', { gameId, color, boardSize, gameType });
      setGameId(gameId);
      setPlayerColor(color);
      setBoardSize(boardSize);
      setGameType(gameType || 'y');
      setGameState('playing');
      setStatusMessage('Joined game!');
      
      // For NEGOTIATOR games, we should be in waiting state first
      if (gameType === 'negotiator') {
        console.log('📍 NEGOTIATOR game joined, setting state to waiting');
        setGameState('waiting');
      }
    });

    socket.on('gameStart', ({ turn, players, boardSize, timeUsed, moveHistory, isHandicapPhase, board, negotiatorMap }) => {
      console.log('🎮🎮🎮 GAMESTART EVENT FIRED IN APP.JSX 🎮🎮🎮');
      console.log('🎮 GameStart event received in App.jsx:', { turn, players, boardSize, gameType });
      console.log('🎮 Current gameState before change:', gameState);
      
      // Update game state to playing for all game types
      setGameState('playing');
      setPlayers(players || []);
      console.log('🎮 Set gameState to playing, updated players to:', players?.length || 0);
      
      // Check for negotiator map in payload (authoritative)
      if (negotiatorMap) {
        console.log('🗺️ Received negotiator map from server with', negotiatorMap.length, 'territories');
        setNegotiatorMap(negotiatorMap);
      }
      
      // Handle Y game specific data
      if (!negotiatorMap && gameType === 'y') {
        setTurn(turn);
        if (boardSize) setBoardSize(boardSize);
        if (timeUsed) setTimeUsed(timeUsed);
        if (moveHistory) setMoveHistory(moveHistory);
        if (board) setBoard(board); // Update board if provided (e.g. rollback)
        else if (moveHistory && moveHistory.length === 0) setBoard({}); // Reset if new game
        
        if (isHandicapPhase !== undefined) setIsHandicapPhase(isHandicapPhase);
        setStatusMessage(`Game started! It is ${turn}'s turn.`);
        setReviewIndex(null); // Ensure we are live
      }
    });

    // Handle player joining for all game types
    socket.on('playerJoined', ({ players, playersNeeded }) => {
      console.log('👥 PlayerJoined event received:', { players, playersNeeded });
      if (players) {
        setPlayers(players);
      }
    });

    socket.on('negotiatorGameReset', ({ message, players }) => {
      console.log('🔄 Negotiator game reset:', message);
      setGameState('waiting');
      setNegotiatorMap(null);
      if (players) setPlayers(players);
      setStatusMessage(message);
    });

    socket.on('moveMade', ({ coord, color, turn, timeUsed, holdState, moveData, isHandicapPhase }) => {
      setBoard(prev => ({ ...prev, [coord]: color }));
      setTurn(turn);
      if (timeUsed) setTimeUsed(timeUsed);
      if (holdState !== undefined) setHoldState(holdState);
      if (moveData) setMoveHistory(prev => [...prev, moveData]);
      if (isHandicapPhase !== undefined) setIsHandicapPhase(isHandicapPhase);
      setCurrentTurnTime(0); // Reset local timer on move
      setShowInitialMoveBanner(false); // Hide initial move banner
      setStatusMessage(`Move made by ${color}. It is ${turn}'s turn.`);
    });

    socket.on('holdUpdate', ({ holdState, timeUsed, lastMoveTime }) => {
        setHoldState(holdState);
        if (timeUsed) setTimeUsed(timeUsed);
        setResumeRequestedBy(null); // Clear any pending requests
    });

    socket.on('resumeRequested', ({ requestedBy }) => {
        setResumeRequestedBy(requestedBy);
    });

    socket.on('rollbackRequested', ({ requestedBy, moveIndex }) => {
        if (requestedBy !== playerColor) {
            if (window.confirm(`Opponent requests to rollback to move ${moveIndex + 1}. Approve?`)) {
                socket.emit('respondRollback', { gameId, approved: true });
            } else {
                socket.emit('respondRollback', { gameId, approved: false });
            }
        }
    });

    socket.on('rollbackRejected', () => {
        alert("Rollback request rejected.");
    });

    socket.on('gameOver', ({ winner, reason, winningPath }) => {
      if (winner === 'draw') {
        setStatusMessage('Game Over! It is a draw.');
      } else {
        let message = `Game Over! ${winner} wins!`;
        if (reason === 'opponent_blocked') {
            const loser = winner === 'white' ? 'black' : 'white';
            message += ` (${loser} cannot win)`;
        }
        setStatusMessage(message);
      }
      if (winningPath) setWinningPath(winningPath);
      setTurn(null); // Stop turns
    });

    socket.on('error', (msg) => {
      alert(msg);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('gameCreated');
      socket.off('gameJoined');
      socket.off('gameStart');
      socket.off('playerJoined');
      socket.off('moveMade');
      socket.off('holdUpdate');
      socket.off('resumeRequested');
      socket.off('rollbackRequested');
      socket.off('rollbackRejected');
      socket.off('gameOver');
      socket.off('error');
    };
  }, []);

  // Fetch ongoing games when user changes
  useEffect(() => {
    if (user && gameState === 'menu') {
      fetchOngoingGames();
    }
  }, [user, gameState]);

  // Fetch available games on component mount
  useEffect(() => {
    fetchAvailableGames();
  }, []);

  // Check for game URL on component mount (before auth check)
  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const potentialGameId = pathParts[1]; // e.g., /ABC123 -> ABC123
    
    // Check if it looks like a game ID (5 uppercase alphanumeric)
    if (potentialGameId && /^[A-Z0-9]{5}$/.test(potentialGameId)) {
      console.log('Found game ID in URL:', potentialGameId);
      setUrlGameId(potentialGameId);
    }
  }, []);

  // Check for game URL when user or page loads
  useEffect(() => {
    if (user && urlGameId) {
      checkGameUrl();
    }
  }, [user, urlGameId]);

  const requestHold = () => {
    socket.emit('requestHold', { gameId });
  };

  const requestResume = () => {
    socket.emit('requestResume', { gameId });
  };

  const createGame = (local = false) => {
    console.log('createGame called with local:', local);
    console.log('isConnected:', isConnected);
    console.log('socket.connected:', socket.connected);
    if (!isConnected) {
      alert("Not connected to server");
      return;
    }
    const size = parseInt(boardSize) || 10;
    console.log("Creating game with type:", selectedGameType, "size:", size, "handicap:", handicap, "local:", local, "persistent:", isPersistent);
    
    // For NEGOTIATOR, use map names instead of board size
    const gameOptions = {
      gameType: selectedGameType,
      isLocal: local,
      isPersistent: !local && isPersistent
    };
    
    if (selectedGameType === 'negotiator') {
      gameOptions.mapId = selectedMap;
      gameOptions.maxPlayers = maxPlayers;
      gameOptions.turnLength = turnLength;
    } else {
      gameOptions.boardSize = size;
      gameOptions.handicap = handicap;
    }
    
    console.log('Emitting createGame with options:', gameOptions);
    socket.emit('createGame', gameOptions);
    setIsLocal(local);
  };

  const fetchOngoingGames = async () => {
    if (!user) return;
    
    setLoadingGames(true);
    try {
      const response = await fetch(`${SERVER_URL}/api/user/games`, { credentials: 'include' });
      if (response.ok) {
        const games = await response.json();
        setOngoingGames(games);
      }
    } catch (error) {
      console.error('Failed to fetch ongoing games:', error);
    } finally {
      setLoadingGames(false);
    }
  };

  const fetchAvailableGames = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/api/games`);
      if (response.ok) {
        const games = await response.json();
        setAvailableGames(games);
      }
    } catch (error) {
      console.error('Failed to fetch available games:', error);
      // Fallback to default games if API fails
      setAvailableGames([
        {
          id: 'y',
          name: 'Game of Y',
          description: 'Connect your pieces to form a Y-shape across the board',
          minPlayers: 2,
          maxPlayers: 2,
          supportedSizes: [6, 8, 10, 12, 14, 16, 20]
        },
        {
          id: 'negotiator',
          name: 'NEGOTIATOR',
          description: 'Cyber-corporate strategy game with real-time negotiations',
          minPlayers: 3,
          maxPlayers: 7,
          supportedSizes: ['neo-singapore', 'tokyo-grid', 'london-circuit']
        }
      ]);
    }
  };

  // Check if current URL contains a game ID and try to access it
  const checkGameUrl = async () => {
    if (!urlGameId) return;
    
    try {
      const response = await fetch(`${SERVER_URL}/api/games/${urlGameId}/access`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.canAccess) {
          // User can access this game - join it
          setGameId(urlGameId);
          setPlayerColor(data.gameInfo.playerColor);
          setGameState('playing');
          socket.emit('joinGame', urlGameId);
          setStatusMessage(`Rejoined game ${urlGameId}`);
          setUrlGameId(null); // Clear the stored game ID
        } else if (data.requiresAuth) {
          // Game exists but user needs to authenticate
          setStatusMessage(`Game ${urlGameId} found. Please log in to continue.`);
        } else {
          // User can't access this game
          setStatusMessage(`You don't have access to game ${urlGameId}`);
          // Clear the URL
          window.history.replaceState({}, '', '/');
          setUrlGameId(null);
        }
      } else {
        // Game doesn't exist
        setStatusMessage(`Game ${urlGameId} not found`);
        // Clear the URL
        window.history.replaceState({}, '', '/');
        setUrlGameId(null);
      }
    } catch (error) {
      console.error('Error checking game URL:', error);
      setStatusMessage('Error accessing game from URL');
      setUrlGameId(null);
    }
  };

  const rejoinGame = async (gameId) => {
    try {
      const response = await fetch(`${SERVER_URL}/api/user/games/${gameId}/rejoin`, { 
        method: 'POST', 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setGameId(gameId);
        setPlayerColor(data.playerColor);
        setGameState('playing');
        socket.emit('joinGame', gameId);
        // Update URL to reflect the current game
        window.history.pushState({}, '', `/${gameId}`);
      }
    } catch (error) {
      console.error('Failed to rejoin game:', error);
    }
  };

  const fetchWaitingGames = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/api/games/waiting`);
      if (response.ok) {
        const games = await response.json();
        setWaitingGames(games);
      }
    } catch (error) {
      console.error('Failed to fetch waiting games:', error);
      // Mock some waiting games for demo
      setWaitingGames([
        {
          gameId: 'ABC12',
          name: 'Quick Game',
          gameType: 'y',
          currentPlayers: 1,
          maxPlayers: 2,
          createdAt: new Date().toISOString(),
          settings: { boardSize: 10 }
        }
      ]);
    }
  };

  const joinSpecificGame = (gameId) => {
    setInputGameId(gameId);
    joinGame();
  };

  const fetchVersionInfo = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/api/version`);
      if (response.ok) {
        const data = await response.json();
        setVersionInfo({
          ...data,
          client: {
            version: VERSION,
            buildDate: BUILD_DATE,
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            connection: isConnected ? 'Connected' : 'Disconnected',
            url: window.location.href,
            timestamp: new Date().toISOString()
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch version info:', error);
    }
  };

  const openTreatyRoom = (opponentColor) => {
    setNegotiationPartner(opponentColor);
    setTreatyRoomOpen(true);
    socket.emit('joinNegotiation', { gameId, player: playerColor, partner: opponentColor });
  };

  const closeTreatyRoom = () => {
    setTreatyRoomOpen(false);
    setNegotiationPartner(null);
    socket.emit('leaveNegotiation', { gameId, player: playerColor, partner: negotiationPartner });
  };

  const leaveGame = () => {
    setGameState('menu');
    setGameId('');
    setPlayerColor(null);
    setBoard({});
    setTurn('white');
    setStatusMessage('');
    setPlayers([]);
    setTimeUsed({ white: 0, black: 0 });
    setCurrentTurnTime(0);
    setHoldState(null);
    setResumeRequestedBy(null);
    setMoveHistory([]);
    setReviewIndex(null);
    setHandicap(0);
    setIsHandicapPhase(false);
    setWinningPath(null);
    turnStartTimeRef.current = null;
    socket.emit('leaveGame');
    // Clear the game URL
    window.history.pushState({}, '', '/');
  };

  const joinGame = () => {
    const sanitizedGameId = inputGameId.trim().toUpperCase();
    if (!sanitizedGameId) {
      alert('Please enter a valid game ID');
      return;
    }
    setGameId(sanitizedGameId);
    setGameState('waiting');
    socket.emit('joinGame', sanitizedGameId);
    // Update URL to reflect joining game
    window.history.pushState({}, '', `/${sanitizedGameId}`);
  };

  const handleMove = (coord) => {
    if (isLocal || turn === playerColor) {
      const timeSpent = turnStartTimeRef.current ? Date.now() - turnStartTimeRef.current : 0;
      socket.emit('makeMove', { gameId, coord, timeSpent });
    }
  };

  const handleLogin = () => {
    window.location.href = `${SERVER_URL}/auth/google`;
  };

  const handleLogout = () => {
    window.location.href = `${SERVER_URL}/auth/logout`;
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getDisplayBoard = () => {
    if (reviewIndex === null) return board;
    const histBoard = {};
    for (let i = 0; i <= reviewIndex; i++) {
        const m = moveHistory[i];
        if (m) histBoard[m.coord] = m.color;
    }
    return histBoard;
  };

  const handlePrevMove = () => {
    if (reviewIndex === null) {
        // Currently live, go to last move
        if (moveHistory.length > 0) setReviewIndex(moveHistory.length - 1);
        else setReviewIndex(-1); // Empty board
    } else {
        if (reviewIndex > -1) setReviewIndex(reviewIndex - 1);
    }
  };

  const handleNextMove = () => {
    if (reviewIndex === null) return; // Already live
    if (reviewIndex < moveHistory.length - 1) {
        setReviewIndex(reviewIndex + 1);
    } else {
        setReviewIndex(null); // Go back to live
    }
  };

  return (
    <div className="App" style={{ textAlign: 'center', padding: '0', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Left: User Profile / Settings */}
      <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 100 }}>
        {user && user.photos && user.photos.length > 0 ? (
            <img 
                src={user.photos[0].value} 
                alt="Profile" 
                onClick={() => setShowSettings(true)}
                style={{ 
                    width: '50px', 
                    height: '50px', 
                    borderRadius: '50%', 
                    cursor: 'pointer', 
                    border: '2px solid #fff', 
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)' 
                }}
                title="Settings"
            />
        ) : (
            <button onClick={() => setShowSettings(true)} style={{ fontSize: '24px', background: 'none', border: 'none', cursor: 'pointer' }}>
                ⚙️
            </button>
        )}
      </div>

      <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 100, display: 'flex', gap: '10px', alignItems: 'center' }}>
        {user ? (
          <div>
            <span style={{ marginRight: '10px' }}>Welcome, {user.displayName} </span>
            <button onClick={handleLogout}>Logout</button>
          </div>
        ) : (
          <button onClick={handleLogin}>Login with Google</button>
        )}
      </div>

      {!((gameState === 'waiting' || gameState === 'playing') && gameType === 'negotiator') && (
        <h1>🎯 Gaming Platform</h1>
      )}
      {!isConnected && <div style={{ color: 'red', marginBottom: '10px' }}>Disconnected from server...</div>}
      
      {gameState === 'landing' && (
        <div style={{ 
          padding: '40px', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}>
          <div style={{ 
            maxWidth: '600px', 
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            padding: '40px',
            borderRadius: '10px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '2.5em', marginBottom: '20px', color: '#333' }}>Welcome to Game of Y</h2>
            <p style={{ fontSize: '1.2em', marginBottom: '30px', color: '#666', lineHeight: '1.6' }}>
              Game of Y is a strategic connection game where players compete to create a continuous path 
              across the hexagonal board. Challenge friends online or play against opponents from around the world.
            </p>
            
            {!isConnected ? (
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#ffebee', 
                border: '1px solid #f44336',
                borderRadius: '5px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#c62828', margin: 0 }}>
                  🔌 Connecting to server... Please wait a moment.
                </p>
              </div>
            ) : urlGameId ? (
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                border: '1px solid #2196f3',
                borderRadius: '5px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#1976d2', margin: 0, fontWeight: 'bold' }}>
                  🎮 Game {urlGameId} found! Please sign in to join the game.
                </p>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#333' }}>Ready to play?</h3>
                  <p style={{ color: '#666' }}>Choose from multiple strategy games including Game of Y and NEGOTIATOR!</p>
                  <p style={{ color: '#888', fontSize: '0.9em' }}>Sign in with Google to save your games and compete with others.</p>
                </div>
                
                <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button 
                    onClick={handleLogin}
                    style={{ 
                      padding: '15px 30px', 
                      fontSize: '1.1em', 
                      backgroundColor: '#4285f4', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '5px', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    🚀 Sign in with Google
                  </button>
                  
                  <button 
                    onClick={() => {
                      if (urlGameId) {
                        // Try to join the game from URL as guest
                        setGameId(urlGameId);
                        setGameState('waiting');
                        socket.emit('joinGame', urlGameId);
                        setUrlGameId(null);
                      } else {
                        setGameState('menu');
                      }
                    }}
                    style={{ 
                      padding: '15px 30px', 
                      fontSize: '1.1em', 
                      backgroundColor: '#666', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '5px', 
                      cursor: 'pointer'
                    }}
                  >
                    {urlGameId ? `Join Game ${urlGameId} as Guest` : 'Continue as Guest'}
                  </button>
                </div>
                
                <p style={{ marginTop: '20px', fontSize: '0.9em', color: '#888' }}>
                  Guest mode allows you to play, but your games won't be saved.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {gameState === 'menu' && (
        <div style={{ padding: '20px' }}>
          {!isCreating ? (
            <>
              <h2 style={{ color: '#333', marginBottom: '20px' }}>🎮 Select a Game</h2>
              
              {/* Available Games Section - Show to all users */}
              <div style={{ margin: '20px' }}>
                <div style={{ border: '2px solid #2196F3', borderRadius: '10px', padding: '20px', marginBottom: '20px', backgroundColor: '#f9f9f9' }}>
                  <h3 style={{ color: '#2196F3', marginTop: 0, fontSize: '1.3em' }}>🎮 Choose Your Game</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                    {availableGames.map(gameType => (
                      <div key={gameType.id} style={{
                        border: selectedGameType === gameType.id ? '2px solid #2196F3' : '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '15px',
                        backgroundColor: selectedGameType === gameType.id ? '#f0f8ff' : '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.3s'
                      }}
                      onClick={() => setSelectedGameType(gameType.id)}
                      >
                        <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>{gameType.name}</h4>
                        <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '0.9em' }}>{gameType.description}</p>
                        <div style={{ fontSize: '0.8em', color: '#888' }}>
                          <span>👥 {gameType.minPlayers}-{gameType.maxPlayers} players</span>
                          <br />
                          <span>
                            {gameType.id === 'y' ? '📏 Sizes' : '🗺️ Maps'}: {gameType.supportedSizes.join(', ')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Create or Join Decision */}
              <div style={{ margin: '20px', textAlign: 'center' }}>
                <h3 style={{ color: '#333', marginBottom: '15px' }}>What would you like to do?</h3>
                <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => {
                      setIsCreating(true);
                    }}
                    style={{ 
                      padding: '15px 25px', 
                      fontSize: '16px', 
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    🎯 Create New Game
                  </button>
                  <button 
                    onClick={() => { setIsJoining(true); fetchWaitingGames(); }}
                    style={{ 
                      padding: '15px 25px', 
                      fontSize: '16px', 
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    🔍 Join Waiting Game
                  </button>
                </div>
              </div>

              {/* Join by Game ID (fallback option) */}
              <div style={{ margin: '20px', textAlign: 'center', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#666' }}>Or join by Game ID</h4>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}>
                  <input 
                    type="text" 
                    placeholder="Enter Game ID (e.g., ABC12)" 
                    value={inputGameId} 
                    onChange={(e) => setInputGameId(e.target.value)}
                    style={{ 
                      padding: '10px', 
                      fontSize: '14px', 
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      minWidth: '200px'
                    }}
                  />
                  <button 
                    onClick={joinGame} 
                    style={{ 
                      padding: '10px 15px', 
                      fontSize: '14px', 
                      backgroundColor: '#FF9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Join
                  </button>
                </div>
              </div>

              {/* Show waiting games when joining */}
              {isJoining && (
                <div style={{ margin: '20px' }}>
                  <div style={{ border: '2px solid #FF9800', borderRadius: '10px', padding: '20px', backgroundColor: '#fff3e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h3 style={{ color: '#FF9800', margin: 0 }}>🎮 Games Looking for Players</h3>
                      <div>
                        <button 
                          onClick={() => fetchWaitingGames()}
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '12px',
                            backgroundColor: '#FF9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                          }}
                        >
                          🔄 Refresh
                        </button>
                        <button 
                          onClick={() => setIsJoining(false)}
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '12px',
                            backgroundColor: '#666',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          ✕ Cancel
                        </button>
                      </div>
                    </div>
                    
                    {waitingGames.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '30px', 
                        color: '#666',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px'
                      }}>
                        <div style={{ fontSize: '3em', marginBottom: '15px' }}>🎯</div>
                        <h4 style={{ margin: '0 0 10px 0' }}>No games waiting for players</h4>
                        <p style={{ margin: 0, fontSize: '0.9em' }}>
                          Be the first to create a {availableGames.find(g => g.id === selectedGameType)?.name || 'game'}!
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                        {waitingGames
                          .filter(game => game.gameType === selectedGameType)
                          .map(game => (
                            <div key={game.gameId} style={{
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              padding: '15px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              transition: 'all 0.3s'
                            }}
                            onClick={() => joinSpecificGame(game.gameId)}
                            >
                              <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>{game.name || `Game ${game.gameId}`}</h4>
                              <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '0.9em' }}>
                                {game.description || `${game.gameType.toUpperCase()} game`}
                              </p>
                              <div style={{ fontSize: '0.8em', color: '#888', marginBottom: '10px' }}>
                                <div>👥 {game.currentPlayers}/{game.maxPlayers} players</div>
                                <div>🕐 Created {new Date(game.createdAt).toLocaleTimeString()}</div>
                                {game.settings && game.settings.boardSize && (
                                  <div>📏 Board size: {game.settings.boardSize}</div>
                                )}
                              </div>
                              <button 
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  backgroundColor: '#4CAF50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                Join Game
                              </button>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                </div>
              )}

              {user && (
                <div style={{ margin: '20px' }}>
                  {/* Your Ongoing Games Section */}
                  <div style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h3 style={{ color: '#4CAF50', margin: 0 }}>🎯 Your Active Games</h3>
                      <button 
                        onClick={fetchOngoingGames} 
                        style={{ 
                          padding: '6px 12px', 
                          fontSize: '12px',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        title="Refresh games list"
                      >
                        🔄 Refresh
                      </button>
                    </div>
                    
                    {loadingGames ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        <div>⏳ Loading your games...</div>
                      </div>
                    ) : ongoingGames.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '30px', 
                        color: '#888',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                        border: '2px dashed #ddd'
                      }}>
                        <div style={{ fontSize: '2em', marginBottom: '10px' }}>🎲</div>
                        <p>No active games found</p>
                        <p style={{ fontSize: '0.9em' }}>Start a new game below!</p>
                      </div>
                    ) : (
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {ongoingGames.map(game => {
                          const isYourTurn = game.turn === game.playerColor;
                          const urgentTurn = isYourTurn && new Date() - new Date(game.lastActivity) > 3600000; // >1 hour
                          
                          return (
                            <div key={game.gameId} style={{ 
                              border: urgentTurn ? '2px solid #ff9800' : '1px solid #eee', 
                              borderRadius: '8px', 
                              padding: '15px', 
                              margin: '10px 0',
                              backgroundColor: isYourTurn ? '#e8f5e8' : '#f8f8f8',
                              boxShadow: urgentTurn ? '0 2px 8px rgba(255,152,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                              transition: 'all 0.3s'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <strong style={{ color: '#333' }}>Game {game.gameId}</strong>
                                    {urgentTurn && <span style={{ 
                                      background: '#ff9800', 
                                      color: 'white', 
                                      padding: '2px 6px', 
                                      borderRadius: '10px', 
                                      fontSize: '0.7em',
                                      fontWeight: 'bold'
                                    }}>⏰ URGENT</span>}
                                    <span style={{
                                      background: isYourTurn ? '#4CAF50' : '#666',
                                      color: 'white',
                                      padding: '2px 6px',
                                      borderRadius: '10px',
                                      fontSize: '0.7em',
                                      fontWeight: 'bold'
                                    }}>
                                      {isYourTurn ? '🎯 YOUR TURN' : '⏳ WAITING'}
                                    </span>
                                  </div>
                                  
                                  <div style={{ fontSize: '0.9em', color: '#555', marginBottom: '5px' }}>
                                    <span>🆚 <strong>{game.opponentName}</strong></span>
                                    <span style={{ margin: '0 15px' }}>•</span>
                                    <span>You: <strong style={{ color: game.playerColor === 'white' ? '#666' : '#333' }}>{game.playerColor}</strong></span>
                                    <span style={{ margin: '0 15px' }}>•</span>
                                    <span>📏 {game.boardSize}×{game.boardSize}</span>
                                  </div>
                                  
                                  <div style={{ fontSize: '0.8em', color: '#777' }}>
                                    📅 Last activity: {new Date(game.lastActivity).toLocaleDateString()} at {new Date(game.lastActivity).toLocaleTimeString()}
                                  </div>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button 
                                    onClick={() => {
                                      const gameUrl = `${window.location.origin}/${game.gameId}`;
                                      navigator.clipboard.writeText(gameUrl);
                                      // Show brief feedback
                                      const btn = document.activeElement;
                                      const originalText = btn.textContent;
                                      btn.textContent = '✅ Copied!';
                                      setTimeout(() => { btn.textContent = originalText; }, 2000);
                                    }}
                                    style={{ 
                                      padding: '10px 12px', 
                                      backgroundColor: '#ff9800',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.8em',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                    title={`Copy game URL: ${window.location.origin}/${game.gameId}`}
                                  >
                                    🔗 Copy URL
                                  </button>
                                  
                                  <button 
                                    onClick={() => rejoinGame(game.gameId)}
                                  style={{ 
                                    padding: '10px 16px', 
                                    backgroundColor: isYourTurn ? '#4CAF50' : '#2196F3',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '0.9em',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                  }}
                                >
                                  {isYourTurn ? '🎯 Play Now' : '👀 View Game'}
                                </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ marginBottom: '20px' }}>
              <h3>🎮 {availableGames.find(g => g.id === selectedGameType)?.name || 'Game'} Settings</h3>
              
              {selectedGameType === 'y' && (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ marginRight: '10px' }}>Board Size (6-100): </label>
                    <input 
                      type="number" 
                      min="6" 
                      max="100" 
                      value={boardSize} 
                      onChange={(e) => setBoardSize(parseInt(e.target.value))}
                      style={{ padding: '5px', width: '60px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ marginRight: '10px' }}>Handicap (0-6): </label>
                    <input 
                      type="number" 
                      min="0" 
                      max="6" 
                      value={handicap} 
                      onChange={(e) => setHandicap(parseInt(e.target.value))}
                      style={{ padding: '5px', width: '60px' }}
                    />
                  </div>
                </>
              )}

              {selectedGameType === 'negotiator' && (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ marginRight: '10px' }}>Map: </label>
                    <select 
                      style={{ padding: '5px', minWidth: '120px' }}
                      value={selectedMap}
                      onChange={(e) => setSelectedMap(e.target.value)}
                    >
                      <option value="neo-singapore">Neo Singapore</option>
                      <option value="tokyo-grid">Tokyo Grid</option>
                      <option value="london-circuit">London Circuit</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ marginRight: '10px' }}>Max Players (3-7): </label>
                    <input 
                      type="number" 
                      min="3" 
                      max="7" 
                      value={maxPlayers}
                      onChange={(e) => setMaxPlayers(parseInt(e.target.value))}
                      style={{ padding: '5px', width: '60px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ marginRight: '10px' }}>Turn Length: </label>
                    <select 
                      style={{ padding: '5px', minWidth: '120px' }}
                      value={turnLength}
                      onChange={(e) => setTurnLength(e.target.value)}
                    >
                      <option value="15min">15 Minutes</option>
                      <option value="30min">30 Minutes</option>
                      <option value="1hour">1 Hour</option>
                      <option value="4hours">4 Hours</option>
                      <option value="24hours">24 Hours</option>
                    </select>
                  </div>
                </>
              )}
              {user && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                    <input 
                      type="checkbox" 
                      checked={isPersistent} 
                      onChange={(e) => setIsPersistent(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Multi-session game (can return later)
                  </label>
                  <p style={{ fontSize: '0.8em', color: '#666', margin: '5px 0 0 20px' }}>
                    Games are kept for 24 hours after last activity
                  </p>
                </div>
              )}
              <button onClick={() => createGame(false)} style={{ padding: '10px', fontSize: '16px', margin: '10px', backgroundColor: '#4CAF50', color: 'white' }}>Start Online Game</button>
              <button onClick={() => createGame(true)} style={{ padding: '10px', fontSize: '16px', margin: '10px', backgroundColor: '#2196F3', color: 'white' }}>Start Local Game</button>
              <button onClick={() => setIsCreating(false)} style={{ padding: '10px', fontSize: '16px', margin: '10px' }}>Cancel</button>
            </div>
          )}
        </div>
      )}



      {gameState === 'waiting' && (
        <div style={{ padding: '20px' }}>
          <h2>Waiting for opponent...</h2>
          <p>
            Share this Game ID: <strong>{gameId}</strong>
            <button
              onClick={handleCopyUrl}
              style={{ marginLeft: 10, padding: '2px 8px', fontSize: '14px', cursor: 'pointer' }}
              title="Copy join URL to clipboard"
            >Copy URL</button>
            {copySuccess && <span style={{ marginLeft: 8, color: 'green', fontSize: '12px' }}>{copySuccess}</span>}
          </p>
        </div>
      )}

      {/* Initial move banner */}
      {showInitialMoveBanner && (
        <div style={{ background: '#fffbe6', color: '#856404', border: '1px solid #ffeeba', padding: '16px', margin: '20px auto', maxWidth: 400, borderRadius: 8, fontWeight: 500 }}>
          It's your turn! Make the first move to start the game.
        </div>
      )}

      {(gameState === 'waiting' || gameState === 'playing') && gameType === 'negotiator' && (
        <div>
          <div style={{ fontSize: '10px', color: 'gray', position: 'fixed', top: 0, right: 0, background: 'white', padding: '5px' }}>
            Debug: gameState={gameState}, gameType={gameType}, players={players.length}
          </div>
          <NegotiatorGame
            gameId={gameId}
            playerId={socket.id}
            players={players}
            gameState={gameState}
            socket={socket}
            initialMap={negotiatorMap}
            onBackToMenu={() => setGameState('menu')}
          />
        </div>
      )}

      {gameState === 'playing' && gameType === 'y' && (
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, width: '100%', overflow: 'hidden' }}>
          {/* Left Panel: My Stats */}
          <div style={{ flex: 1, padding: '10px', borderRight: '1px solid #ccc', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, overflowY: 'auto' }}>
            <h3>{isLocal ? 'White' : `You (${playerColor})`}</h3>
            {user && <p>{user.displayName}</p>}
            
            <div style={{ 
                padding: '20px', 
                border: turn === (isLocal ? 'white' : playerColor) ? '3px solid gold' : '1px solid #ccc', 
                borderRadius: '10px',
                backgroundColor: turn === (isLocal ? 'white' : playerColor) ? '#fffde7' : '#f5f5f5',
                width: '80%',
                marginBottom: '20px'
            }}>
                {showTimer ? (
                    <>
                        <div style={{ fontSize: '1.2em', marginBottom: '10px' }}>Total Time</div>
                        <div style={{ fontSize: '2em', fontWeight: 'bold' }}>{formatTime(timeUsed[isLocal ? 'white' : playerColor])}</div>
                        {turn === (isLocal ? 'white' : playerColor) && (
                            <div style={{ color: 'green', marginTop: '10px', fontSize: '1.5em' }}>
                                +{formatTime(currentTurnTime)}
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ fontSize: '1.5em', fontWeight: 'bold' }}>
                        {turn === (isLocal ? 'white' : playerColor) ? (isLocal ? "White's Turn" : "Your Turn") : "Waiting..."}
                    </div>
                )}
            </div>

            {(!holdState || !holdState.active) && (
                <button onClick={requestHold} style={{ padding: '15px', width: '80%', fontSize: '1.2em', backgroundColor: '#f0f0f0', cursor: 'pointer' }}>
                    Hold Game
                </button>
            )}

            {(!holdState || !holdState.active) && selectedGameType === 'Negotiator' && players.length > 1 && (
                <button 
                    onClick={() => {
                        const opponent = players.find(p => p.color !== playerColor);
                        openTreatyRoom(opponent.color);
                    }} 
                    style={{ 
                        padding: '15px', 
                        width: '80%', 
                        fontSize: '1.2em', 
                        backgroundColor: '#4CAF50', 
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        marginTop: '10px',
                        cursor: 'pointer' 
                    }}
                >
                    Negotiate
                </button>
            )}
            
            {holdState && holdState.active && (
                <div style={{ margin: '10px', padding: '10px', border: '1px solid orange', backgroundColor: '#fff3e0', width: '80%' }}>
                    <p><strong>{isLocal ? "Game Paused" : `Game Held by ${holdState.initiator}`}</strong></p>
                    {resumeRequestedBy ? (
                        <div>
                            <p>{resumeRequestedBy === playerColor ? "Waiting for opponent..." : "Opponent wants to resume!"}</p>
                            {resumeRequestedBy !== playerColor && (
                                <button onClick={requestResume} style={{ padding: '10px', backgroundColor: '#4CAF50', color: 'white', width: '100%' }}>
                                    Accept Resume
                                </button>
                            )}
                        </div>
                    ) : (
                        <button onClick={requestResume} style={{ padding: '10px', backgroundColor: '#2196F3', color: 'white', width: '100%' }}>
                            Request Resume
                        </button>
                    )}
                </div>
            )}
          </div>

          {/* Center Panel: Board */}
          <div style={{ flex: 3, padding: '5px', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}>
            <div style={{ marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <button
                onClick={leaveGame}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
                title="Leave game and return to menu"
              >
                ← Back to Games
              </button>
              <div>
                <strong>Game ID: {gameId}</strong> | {statusMessage}
              </div>
              <div style={{ width: '120px' }}>{/* Spacer for balance */}</div>
            </div>
            
            {holdState && holdState.active && (isLocal || holdState.initiator === playerColor || turn !== holdState.initiator) ? (
                <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    backgroundColor: '#333', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    borderRadius: '10px'
                }}>
                    <h2>Game Paused</h2>
                    <p>
                        {isLocal 
                            ? "Game paused. Press Resume to continue."
                            : (holdState.initiator === playerColor 
                                ? "Display is hidden because you held the game." 
                                : "Game paused. Waiting for resume.")}
                    </p>
                </div>
            ) : (
                <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Board 
                            board={getDisplayBoard()} 
                            onMove={reviewIndex === null ? handleMove : () => {}} 
                            turn={turn} 
                            playerColor={playerColor} 
                            boardSize={boardSize}
                            winningPath={winningPath}
                        />
                    </div>
                    <div style={{ padding: '10px', display: 'flex', gap: '20px', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                        <button 
                            onClick={handlePrevMove} 
                            disabled={reviewIndex === -1}
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                fontSize: '24px', 
                                cursor: reviewIndex === -1 ? 'default' : 'pointer',
                                color: reviewIndex === -1 ? '#ccc' : '#333'
                            }}
                        >
                            &#9664;
                        </button>
                        {reviewIndex !== null && (
                            <button
                                onClick={() => {
                                    if (window.confirm("Request to resume game from this move? This will delete all subsequent moves.")) {
                                        socket.emit('requestRollback', { gameId, moveIndex: reviewIndex });
                                    }
                                }}
                                style={{
                                    padding: '5px 10px',
                                    backgroundColor: '#FF9800',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer'
                                }}
                            >
                                Resume Here
                            </button>
                        )}
                        <button 
                            onClick={() => setReviewIndex(null)}
                            disabled={reviewIndex === null}
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                fontSize: '16px', 
                                fontWeight: 'bold',
                                cursor: reviewIndex === null ? 'default' : 'pointer',
                                color: reviewIndex === null ? '#ccc' : '#333',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            LIVE
                        </button>
                        <button 
                            onClick={handleNextMove} 
                            disabled={reviewIndex === null}
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                fontSize: '24px', 
                                cursor: reviewIndex === null ? 'default' : 'pointer',
                                color: reviewIndex === null ? '#ccc' : '#333'
                            }}
                        >
                            &#9654;
                        </button>
                    </div>
                    {reviewIndex !== null && <div style={{ color: '#666', fontSize: '0.9em' }}>Viewing Move {reviewIndex + 1} / {moveHistory.length}</div>}
                </div>
            )}
          </div>

          {/* Right Panel: Opponent Stats & Log */}
          <div style={{ flex: 1, padding: '10px', borderLeft: '1px solid #ccc', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, overflowY: 'auto' }}>
            <h3>{isLocal ? 'Black' : `Opponent (${playerColor === 'white' ? 'black' : 'white'})`}</h3>
            {players.find(p => p.color !== playerColor) && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '10px' }}>
                    {players.find(p => p.color !== playerColor).photo && (
                        <img 
                            src={players.find(p => p.color !== playerColor).photo} 
                            alt="Opponent" 
                            style={{ 
                                width: '60px', 
                                height: '60px', 
                                borderRadius: '50%', 
                                marginBottom: '5px',
                                border: '2px solid #ccc'
                            }} 
                        />
                    )}
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{players.find(p => p.color !== playerColor).name}</p>
                </div>
            )}

            <div style={{ 
                padding: '20px', 
                border: turn !== (isLocal ? 'white' : playerColor) ? '3px solid gold' : '1px solid #ccc', 
                borderRadius: '10px',
                backgroundColor: turn !== (isLocal ? 'white' : playerColor) ? '#fffde7' : '#f5f5f5',
                width: '80%',
                marginBottom: '20px'
            }}>
                {showTimer ? (
                    <>
                        <div style={{ fontSize: '1.2em', marginBottom: '10px' }}>Total Time</div>
                        <div style={{ fontSize: '2em', fontWeight: 'bold' }}>{formatTime(timeUsed[isLocal ? 'black' : (playerColor === 'white' ? 'black' : 'white')])}</div>
                        {turn !== (isLocal ? 'white' : playerColor) && (
                            <div style={{ color: 'green', marginTop: '10px', fontSize: '1.5em' }}>
                                +{formatTime(currentTurnTime)}
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ fontSize: '1.5em', fontWeight: 'bold' }}>
                        {turn !== (isLocal ? 'white' : playerColor) ? (isLocal ? "Black's Turn" : "Opponent's Turn") : "Waiting..."}
                    </div>
                )}
            </div>

            <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                <h4>Move Log</h4>
                {holdState && holdState.active && (isLocal || holdState.initiator === playerColor || turn !== holdState.initiator) ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                        Hidden while paused
                    </div>
                ) : (
                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #eee', padding: '5px', textAlign: 'left', fontSize: '0.9em' }}>
                        {[...moveHistory].reverse().map((move, index) => {
                            const originalIndex = moveHistory.length - 1 - index;
                            return (
                                <div 
                                    key={originalIndex} 
                                    onClick={() => setReviewIndex(originalIndex)}
                                    style={{ 
                                        padding: '2px 5px', 
                                        borderBottom: '1px solid #f9f9f9',
                                        cursor: 'pointer',
                                        backgroundColor: reviewIndex === originalIndex ? '#e3f2fd' : 'transparent'
                                    }}
                                >
                                    <strong>{originalIndex + 1}. {move.color}:</strong> {move.coord}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '10px',
                minWidth: '400px',
                maxWidth: '600px',
                maxHeight: '80vh',
                overflowY: 'auto',
                color: 'black',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ marginTop: 0 }}>Settings</h2>
                
                {!showAbout ? (
                  <>
                    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label htmlFor="showTimer" style={{ fontSize: '1.1em', cursor: 'pointer' }}>Show Timer</label>
                        <input 
                            type="checkbox" 
                            id="showTimer" 
                            checked={showTimer} 
                            onChange={(e) => setShowTimer(e.target.checked)} 
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                      <button 
                        onClick={() => { fetchVersionInfo(); setShowAbout(true); }} 
                        style={{ 
                          flex: 1, 
                          padding: '10px', 
                          backgroundColor: '#4CAF50', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '5px', 
                          cursor: 'pointer', 
                          fontSize: '1em' 
                        }}
                      >
                        About & Debug Info
                      </button>
                    </div>
                    
                    <button onClick={() => setShowSettings(false)} style={{ width: '100%', padding: '10px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1em' }}>
                        Close
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: '20px' }}>
                      <button 
                        onClick={() => setShowAbout(false)} 
                        style={{ 
                          padding: '5px 10px', 
                          backgroundColor: '#ccc', 
                          border: 'none', 
                          borderRadius: '3px', 
                          cursor: 'pointer',
                          marginBottom: '15px'
                        }}
                      >
                        ← Back to Settings
                      </button>
                      
                      <h3>About Game of Y</h3>
                      {versionInfo ? (
                        <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
                          <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                            <h4 style={{ margin: '0 0 5px 0' }}>Client Information</h4>
                            <div><strong>Version:</strong> {versionInfo.client.version}</div>
                            <div><strong>Build Date:</strong> {new Date(versionInfo.client.buildDate).toLocaleString()}</div>
                            <div><strong>Connection:</strong> {versionInfo.client.connection}</div>
                            <div><strong>Platform:</strong> {versionInfo.client.platform}</div>
                            <div><strong>Language:</strong> {versionInfo.client.language}</div>
                            <div><strong>URL:</strong> {versionInfo.client.url}</div>
                          </div>
                          
                          <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                            <h4 style={{ margin: '0 0 5px 0' }}>Server Information</h4>
                            <div><strong>Version:</strong> {versionInfo.server.version}</div>
                            <div><strong>Environment:</strong> {versionInfo.server.environment}</div>
                            <div><strong>Node.js:</strong> {versionInfo.server.nodeVersion}</div>
                            <div><strong>Platform:</strong> {versionInfo.server.platform} ({versionInfo.server.arch})</div>
                            <div><strong>Uptime:</strong> {Math.floor(versionInfo.server.uptime / 60)} minutes</div>
                            <div><strong>Memory:</strong> {Math.round(versionInfo.server.memory.heapUsed / 1024 / 1024)} MB used</div>
                          </div>
                          
                          <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                            <h4 style={{ margin: '0 0 5px 0' }}>Features</h4>
                            <div><strong>Persistent Games:</strong> {versionInfo.features.persistentGames ? '✅' : '❌'}</div>
                            <div><strong>Google Auth:</strong> {versionInfo.features.googleAuth ? '✅' : '❌'}</div>
                            <div><strong>Real-time:</strong> {versionInfo.features.socketIO ? '✅' : '❌'}</div>
                          </div>
                          
                          <div style={{ padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '5px', fontSize: '0.8em', color: '#666' }}>
                            <div><strong>Build Info:</strong></div>
                            <div>Generated: {new Date(versionInfo.buildInfo.timestamp).toLocaleString()}</div>
                            <div>Timezone: {versionInfo.buildInfo.timezone}</div>
                            <div>Game ID: {gameId || 'None'}</div>
                            {user && <div>User: {user.displayName} ({user.id})</div>}
                          </div>
                        </div>
                      ) : (
                        <div>Loading version information...</div>
                      )}
                    </div>
                    
                    <button onClick={() => { setShowAbout(false); setShowSettings(false); }} style={{ width: '100%', padding: '10px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1em' }}>
                        Close
                    </button>
                  </>
                )}
            </div>
        </div>
      )}

      {/* Treaty Room Modal */}
      {treatyRoomOpen && (
        <TreatyRoom
          gameId={gameId}
          playerColor={playerColor}
          partner={negotiationPartner}
          socket={socket}
          onClose={closeTreatyRoom}
        />
      )}
    </div>
  );
}

export default App;
